if (!customElements.get('product-combo')) {
  customElements.define(
    'product-combo',
    class ProductCombo extends HTMLElement {
      connectedCallback() {
        this.discount = Number(this.dataset.discount) || 0;
        this.discountAmount = Number(this.dataset.discountAmount) || 0;
        this.button = this.querySelector('[data-combo-submit]');
        this.buttonText = this.button.querySelector('[data-combo-button-text]');
        this.errorMessage = this.querySelector('[data-combo-error]');
        this.totalElement = this.querySelector('[data-combo-total]');
        this.compareElement = this.querySelector('[data-combo-compare]');

        this.items = [...this.querySelectorAll('[data-combo-item]')]
          .filter((element) => element.querySelector('[data-combo-variants]'))
          .map((element) => ({
            element,
            variants: JSON.parse(element.querySelector('[data-combo-variants]').textContent),
            selects: [...element.querySelectorAll('[data-combo-option]')],
            image: element.querySelector('[data-combo-image]'),
            price: element.querySelector('[data-combo-price]'),
            status: element.querySelector('[data-combo-status]'),
          }));

        this.items.forEach((item) => {
          item.selects.forEach((select) => select.addEventListener('change', () => this.onOptionChange(item)));
          this.updateItem(item);
        });
        this.button.addEventListener('click', this.onSubmit.bind(this));
        this.updateSummary();
      }

      onOptionChange(item) {
        this.showError();
        this.updateItem(item);
        this.updateSummary();
      }

      findVariant(item, options) {
        return item.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
      }

      updateItem(item) {
        const selected = item.selects.map((select) => select.value);
        item.variant = item.selects.length ? this.findVariant(item, selected) : item.variants[0];

        // Marca en cada desplegable los valores que, con el resto de la
        // selección actual, no existen o están agotados (como el selector de Dawn).
        item.selects.forEach((select, index) => {
          [...select.options].forEach((option) => {
            const candidate = [...selected];
            candidate[index] = option.value;
            const variant = this.findVariant(item, candidate);
            let label = option.value;
            if (!variant) label += ` – ${this.button.dataset.unavailableLabel}`;
            else if (!variant.available) label += ` – ${this.button.dataset.soldOutLabel}`;
            option.textContent = label;
          });
        });

        const { variant } = item;
        if (variant?.image && item.image && String(variant.image) !== item.image.dataset.comboImage) {
          const source = item.element
            .querySelector(`template[data-combo-image-template="${variant.image}"]`)
            ?.content.querySelector('img');
          if (source) {
            item.image.srcset = source.getAttribute('srcset');
            item.image.src = source.getAttribute('src');
            item.image.dataset.comboImage = variant.image;
          }
        }
        if (item.price) item.price.textContent = variant ? this.formatMoney(variant.price) : '';
        if (item.status) {
          if (!variant) item.status.textContent = this.button.dataset.unavailableLabel;
          else if (!variant.available) item.status.textContent = this.button.dataset.soldOutLabel;
          else item.status.textContent = '';
        }
      }

      updateSummary() {
        const ready = this.items.length > 0 && this.items.every((item) => item.variant?.available);
        const total = this.items.reduce((sum, item) => sum + (item.variant?.price || 0), 0);
        const discounted = Math.max(0, Math.floor((total * (100 - this.discount)) / 100) - this.discountAmount);

        this.totalElement.textContent = this.formatMoney(discounted);
        if (this.compareElement) this.compareElement.textContent = this.formatMoney(total);

        if (ready) {
          this.button.removeAttribute('aria-disabled');
          this.buttonText.textContent = this.button.dataset.label;
        } else {
          this.button.setAttribute('aria-disabled', 'true');
          const missing = this.items.some((item) => !item.variant);
          this.buttonText.textContent = missing
            ? this.button.dataset.unavailableLabel
            : this.button.dataset.soldOutLabel;
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        this.showError();
        this.setLoading(true);

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items: this.items.map((item) => ({ id: item.variant.id, quantity: 1 })) };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        try {
          const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-combo',
              errors: response.errors || response.description,
              message: response.message,
            });
            this.showError(response.description || response.message);
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-combo', cartData: response });
          this.renderCart(cart, response);
        } catch (error) {
          console.error(error);
          this.showError(window.cartStrings?.error);
        } finally {
          this.setLoading(false);
        }
      }

      renderCart(cart, response) {
        const [firstItem] = response.items;
        cart.renderContents({ ...response, id: firstItem.id, key: firstItem.key });
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

        // La notificación solo pinta la línea de `key`: se sustituye por todas
        // las líneas añadidas para que se vean los productos del combo.
        const container = document.getElementById('cart-notification-product');
        const html = response.sections?.['cart-notification-product'];
        if (cart.tagName !== 'CART-NOTIFICATION' || !container || !html) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rows = response.items
          .map((item) => doc.getElementById(`cart-notification-product-${item.key}`))
          .filter(Boolean)
          .map((row) => `<div class="product-combo-added__item">${row.innerHTML}</div>`);
        if (rows.length) container.innerHTML = `<div class="product-combo-added">${rows.join('')}</div>`;
      }

      setLoading(loading) {
        const spinner = this.button.querySelector('.loading__spinner');
        this.button.classList.toggle('loading', loading);
        spinner?.classList.toggle('hidden', !loading);
        if (loading) this.button.setAttribute('aria-disabled', 'true');
        else this.updateSummary();
      }

      showError(message) {
        this.errorMessage.textContent = message || '';
        this.errorMessage.hidden = !message;
      }

      // Mismo formato que el filtro `money` de Liquid (p. ej. "€{{amount_with_comma_separator}} EUR").
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const amount = (precision, thousands = ',', decimal = '.') => {
          const [integer, decimals] = (cents / 100).toFixed(precision).split('.');
          return integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (decimals ? decimal + decimals : '');
        };
        const formats = {
          amount: () => amount(2),
          amount_no_decimals: () => amount(0),
          amount_with_comma_separator: () => amount(2, '.', ','),
          amount_no_decimals_with_comma_separator: () => amount(0, '.', ','),
          amount_with_space_separator: () => amount(2, ' ', ','),
          amount_with_period_and_space_separator: () => amount(2, ' ', '.'),
          amount_with_apostrophe_separator: () => amount(2, "'", '.'),
        };
        const key = format.match(placeholder)?.[1];
        return format.replace(placeholder, (formats[key] || formats.amount)());
      }
    }
  );
}
